import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { matrimonyAPI, getMediaUrl } from '../../utils/api';
import { useAuth } from '../../utils/AuthContext';


export default function Matches() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCaste, setFilterCaste] = useState('ALL');
  const [requestSent, setRequestSent] = useState({});

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    matrimonyAPI.getCuratedMatches()
      .then(data => { setMatches(data); setLoading(false); })
      .catch(err => {
        console.error('Curated matches error:', err);
        setError('Could not load matches. Please try again later.');
        setLoading(false);
      });
  }, [user]);

  const handleConnect = (id) => {
    setRequestSent((prev) => ({ ...prev, [id]: true }));
  };

  const castes = ['ALL', ...Array.from(new Set(matches.map(m => m.caste).filter(c => c && c !== 'Unspecified')))];

  const filteredMatches = filterCaste === 'ALL'
    ? matches
    : matches.filter((m) => m.caste === filterCaste);

  const getGenderEmoji = (gender) => {
    const g = (gender || '').toLowerCase();
    if (g.includes('female') || g.includes('bride')) return '👰';
    if (g.includes('male') || g.includes('groom')) return '🤵';
    return '👤';
  };

  if (!user) {
    return (
      <div className="sec-wrapper" style={{ textAlign: 'center', padding: '4rem 1rem' }}>
        <span className="badge-light">✦ Confidential Matchmaking Registry ✦</span>
        <h1 className="section-title">Curated Matches</h1>
        <p style={{ color: '#6E615E', margin: '0.8rem 0 2rem' }}>
          Please log in to view your curated match recommendations.
        </p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <button className="btn btn-primary" onClick={() => navigate('/consultation/login')}>Sign In to View Matches →</button>
          <button className="btn btn-secondary" onClick={() => navigate('/consultation/register')}>Register Profile →</button>
        </div>
      </div>
    );
  }

  return (
    <div className="sec-wrapper">
      {/* Unified Vivah Nepal Hero Banner */}
      <section className="hero-section">
        <div className="hero-content">
          <div style={{ marginBottom: '1.2rem' }}>
            <span className="badge-hero-top">
              ✦ CONFIDENTIAL MATCHMAKING REGISTRY ✦
            </span>
          </div>

          <h1 className="hero-title">
            Your Curated <span className="hero-title-gradient">Marriage Matches</span>
          </h1>

          <p className="hero-subtitle">
            Authentic Kundali and profile evaluation managed by <strong>Laxmikant Timilsina</strong> (Tansen-09, Palpa | 9857074103). Contact details remain protected until mutual consent.
          </p>

          <div className="hero-cta-group">
            <button className="btn btn-secondary" onClick={() => navigate('/consultation/register')}>
              Register Marriage Profile →
            </button>
            <a href="tel:9857074103" className="btn btn-tertiary">
              📞 Call Laxmikant (9857074103)
            </a>
          </div>
        </div>
      </section>

      {loading && (
        <div style={{ textAlign: 'center', padding: '4rem 1rem', color: '#9E1B32' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>💑</div>
          <p style={{ fontWeight: 600 }}>Finding your curated matches…</p>
        </div>
      )}

      {!loading && error && (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', borderColor: '#e57373' }}>
          <p style={{ color: '#c62828', fontWeight: 600 }}>{error}</p>
          <button className="btn btn-secondary" onClick={() => window.location.reload()}>Retry</button>
        </div>
      )}

      {!loading && !error && matches.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1.5rem', background: '#FAF7F2', border: '1.5px solid #D4AF37' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.8rem' }}>📋</div>
          <span className="badge-light" style={{ background: '#FAF6ED', color: '#8C6A23', marginBottom: '0.8rem' }}>
            Administrator Assignment Pending
          </span>
          <h2 style={{ fontFamily: 'var(--serif)', color: '#9E1B32', marginBottom: '0.6rem', fontSize: '1.8rem' }}>
            Your Profile Is Under Review & Matchmaking
          </h2>
          <p style={{ color: '#6E615E', maxWidth: '580px', margin: '0 auto 1.4rem', fontSize: '0.92rem', lineHeight: 1.6 }}>
            Curated matches are <strong>personally assigned by administrator Laxmikant Timilsina</strong> after evaluating horoscope compatibility and family criteria. Once assigned in the system, your curated matches will automatically appear on this page.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="tel:9857074103" className="btn btn-primary">
              📞 Call Laxmikant (9857074103) →
            </a>
            <a
              href="https://wa.me/9779857074103?text=Namaste%20Laxmikant%20ji,%20I%20have%20registered%20my%20profile%20and%20would%20like%20to%20request%20curated%20match%20assignments."
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
            >
              💬 WhatsApp Consultation
            </a>
          </div>
        </div>
      )}

      {!loading && !error && matches.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '2.5rem' }}>
            {castes.map((caste) => (
              <button
                key={caste}
                onClick={() => setFilterCaste(caste)}
                className="btn btn-sm"
                style={{
                  background: filterCaste === caste ? '#9E1B32' : '#FFFFFF',
                  color: filterCaste === caste ? '#fff' : '#1C1014',
                  border: '1.5px solid #E8DFD1',
                  fontWeight: 700
                }}
              >
                {caste === 'ALL' ? `✦ All ${matches.length} Matches` : `${caste} Caste`}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '2rem', marginBottom: '3.5rem' }}>
            {filteredMatches.map((p) => (
              <div key={p.id} className="card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                    <span className="badge-verified">✓ Verified Profile</span>
                    <span className="badge-light" style={{ background: '#FAF6ED', color: '#8C6A23' }}>
                      {p.marital_status || 'Never Married'}
                    </span>
                  </div>

                  <div style={{
                    height: '160px',
                    background: 'linear-gradient(135deg, #7A1221, #9E1B32)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    marginBottom: '1rem',
                    position: 'relative'
                  }}>
                    {p.photo && !p.photo_privacy ? (
                      <img src={getMediaUrl(p.photo)} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : p.photo && p.photo_privacy ? (
                      <>
                        <img src={getMediaUrl(p.photo)} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(8px)' }} />
                        <div style={{ position: 'absolute', bottom: '0.5rem', width: '100%', textAlign: 'center', fontSize: '0.72rem', color: '#F7D070', fontWeight: 700 }}>
                          🔒 Photo Privacy Protected
                        </div>
                      </>
                    ) : (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '2.8rem' }}>{getGenderEmoji(p.gender)}</div>
                        <div style={{ fontSize: '0.74rem', color: '#F7D070', fontWeight: 700, marginTop: '0.3rem' }}>
                          📷 No Photo Uploaded
                        </div>
                      </div>
                    )}
                  </div>

                  <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1C1014', margin: '0.2rem 0', fontFamily: 'var(--serif)' }}>
                    {p.name}{p.age ? ` (${p.age} Yrs)` : ''}
                  </h3>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#8C6A23', marginBottom: '0.4rem' }}>
                    {p.caste} Caste{p.gotra ? ` · Gotra: ${p.gotra}` : ''}
                  </div>
                  <div style={{ fontSize: '0.84rem', color: '#6E615E', marginBottom: '0.8rem', lineHeight: 1.6 }}>
                    {p.education && <span>🎓 {p.education}<br /></span>}
                    {p.occupation && <span>💼 {p.occupation}<br /></span>}
                    <span>📍 {p.location}</span>
                  </div>

                  <div className="privacy-shield" style={{ fontSize: '0.74rem', padding: '0.5rem 0.8rem' }}>
                    🔒 Phone & full identity hidden until accepted
                  </div>
                </div>

                <div style={{ marginTop: '1rem' }}>
                  {requestSent[p.id] ? (
                    <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '0.6rem', borderRadius: '12px', textAlign: 'center', fontSize: '0.8rem', fontWeight: 700 }}>
                      ✓ Request Sent to Laxmikant Timilsina!
                    </div>
                  ) : (
                    <button className="btn btn-primary btn-full" onClick={() => handleConnect(p.id)}>
                      Connect / Request Introduction →
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!loading && (
        <div className="card" style={{ background: 'linear-gradient(135deg, #1C1014 0%, #28171C 100%)', color: 'white', border: '1.5px solid #D4AF37', textAlign: 'center' }}>
          <span className="badge" style={{ background: 'rgba(212, 175, 55, 0.25)', color: '#F7D070', marginBottom: '0.6rem' }}>
            ✦ MEMBERSHIP & MATCH SEARCH TERMS ✦
          </span>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: '1.9rem', fontWeight: 700, color: '#F7D070', margin: '0.4rem 0' }}>
            Personalized Matchmaking & Membership Tiers
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.88)', maxWidth: '680px', margin: '0 auto 1.4rem', fontSize: '0.9rem', lineHeight: 1.6 }}>
            Profile Registration is <strong>100% FREE</strong>. Match search options include:
            <br />
            • <strong>Consultation Membership (NPR 21,000)</strong>: Includes curated match search access for <strong>6 Months</strong>.
            <br />
            • <strong>Full Membership (NPR 50,000)</strong>: Includes curated match search access for an <strong>UNLIMITED Period</strong>.
          </p>
          <div style={{ display: 'flex', gap: '0.9rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={() => navigate('/consultation/payment')}>
              🏛️ Pay via Siddhartha Bank QR (LAXMIKANT TIMILSINA) →
            </button>
            <a href="tel:9857074103" className="btn btn-tertiary">
              📞 Call Laxmikant (9857074103)
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
